import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { InverterType } from '../../types/api.types';
import { LgcyEnphaseBomResults } from '../../types/lgcy-bom.types';
import { NetsuiteItemState } from '../../types/netsuite-items.types';
import { INVERTER_BRANDS } from '../../constants/app.constants';
import { SystemKey, SYSTEM_KEYS } from '../../constants/aurora.constants';
import { WorkflowStep } from '../slices/createCRSlice';

// Default values for stable references (prevents infinite loops in useEffect)
const DEFAULT_BILL_OF_MATERIAL = {
  total_rails: 0,
  total_splice_kits: 0,
  total_mid_clamps: 0,
  total_end_clamps: 0,
  total_grounding_lugs: 0,
  is_manual_override: false
};

const DEFAULT_LGCY_BOM = {
  installation_method: 'rafter_mount' as const,
  lis_number: '',
  ess_config: '' as string,
  lender: { id: '', name: '' },
  package: { id: '', name: '', key: '', auto_enphase_accessories: false as boolean | undefined },
  lender_package_items: [] as any[],
  client_netsuite_items: [] as any[],
  input_fields: { is_manual_override: false, total_rows: 0, arrays: 0, strings_circuits: 0, mci_s: 0 },
  bom_results: { is_manual_override: false, sum_of_panels: 0, rails_in_total: 0, end_cap: 0, splice_kit: 0, mid_clamps_total: 0, end_clamps_total: 0, washer_lag: 0, estimated_attachments: 0, universal_wire_clamp: 0, wire_retention_clip: 0, junction_box: 0, grounding_lugs: 0, enphase: null as null | LgcyEnphaseBomResults },
};

const DEFAULT_BOM_DATA = {
  bom_type: 'standard' as const,
  standard: { is_manual_override: false, total_rails: 0, total_splice_kits: 0, total_mid_clamps: 0, total_end_clamps: 0, total_grounding_lugs: 0 },
  lgcy: DEFAULT_LGCY_BOM,
};

// Base selector
export const selectCreateCR = (state: RootState) => state.createCR;

// Workflow selectors
export const selectWorkflow = createSelector(
  selectCreateCR,
  (createCR) => createCR?.workflow || {}
);

export const selectCurrentStep = createSelector(
  selectWorkflow,
  (workflow) => workflow?.current_step
);

export const selectCompletedSteps = createSelector(
  selectWorkflow,
  (workflow) => workflow?.completed_steps || []
);

export const selectValidationAttempted = createSelector(
  selectWorkflow,
  (workflow) => workflow?.validation_attempted
);

// Form data selectors
export const selectFormData = createSelector(
  selectCreateCR,
  (createCR) => createCR?.form_data || {}
);

// Task selectors
export const selectTaskData = createSelector(
  selectFormData,
  (formData) => formData?.task || {}
);

export const selectSelectedTask = createSelector(
  selectTaskData,
  (taskData) => taskData?.selected_task
);

export const selectAuroraDesignId = createSelector(
  selectTaskData,
  (taskData) => taskData?.aurora_design_id || ''
);

// Aurora data selectors
export const selectAuroraData = createSelector(
  selectCreateCR,
  (createCR) => createCR?.aurora_data
);

export const selectAuroraDesignSummary = createSelector(
  selectAuroraData,
  (auroraData) => auroraData?.design_summary
);

export const selectAuroraRoofSummary = createSelector(
  selectAuroraData,
  (auroraData) => auroraData?.roof_design_summary
);

export const selectStructuresRoofsDetails = createSelector(
  selectAuroraData,
  (auroraData) => auroraData?.structures_roofs_details ?? null
);

export const selectAuroraPanelRowsPool = createSelector(
  selectAuroraData,
  (auroraData) => auroraData?.aurora_panel_rows_pool ?? null
);

// Equipment selectors
export const selectEquipmentData = createSelector(
  selectFormData,
  (formData) => formData?.equipment || {}
);

export const selectActiveSystem = createSelector(
  selectEquipmentData,
  (equipment) => (equipment?.active_system || 'system_one') as SystemKey
);

// Active system equipment data
const selectActiveSystemData = createSelector(
  [selectEquipmentData, selectActiveSystem],
  (equipment, activeSystem) => equipment?.[activeSystem] || {}
);

export const selectModuleData = createSelector(
  selectActiveSystemData,
  (systemData) => systemData?.module || {}
);

export const selectInverterData = createSelector(
  selectActiveSystemData,
  (systemData) => systemData?.inverters || {}
);

export const selectBatteryData = createSelector(
  selectActiveSystemData,
  (systemData) => systemData?.batteries || {}
);

export const selectStringsData = createSelector(
  selectActiveSystemData,
  (systemData) => systemData?.strings || {}
);

// Multi-system module data for Mounting step (panel rows & BOM)
export interface SystemModuleInfo {
  quantity: number;
  width?: number;
  height?: number;
  moduleName?: string;
}

export const selectConfiguredSystems = createSelector(
  selectEquipmentData,
  (equipment): SystemKey[] =>
    SYSTEM_KEYS.filter(key => {
      const mod = equipment?.[key]?.module;
      return mod && mod.quantity > 0 && mod.selected_module?.fullName;
    })
);

export const selectAllSystemsModuleData = createSelector(
  selectEquipmentData,
  (equipment): Record<SystemKey, SystemModuleInfo> => {
    const result = {} as Record<SystemKey, SystemModuleInfo>;
    SYSTEM_KEYS.forEach(key => {
      const mod = equipment?.[key]?.module;
      result[key] = {
        quantity: mod?.quantity || 0,
        width: mod?.selected_module?.width,
        height: mod?.selected_module?.height,
        moduleName: mod?.selected_module?.fullName,
      };
    });
    return result;
  }
);

export const selectProductionData = createSelector(
  selectEquipmentData,
  (equipment) => equipment?.production || { production_value: '' }
);

// Electrical selectors
export const selectElectricalData = createSelector(
  selectFormData,
  (formData) => formData?.electrical || {}
);

export const selectMspData = createSelector(
  selectElectricalData,
  (electrical) => electrical?.msp || {}
);

export const selectMbeData = createSelector(
  selectElectricalData,
  (electrical) => electrical?.mbe || {}
);

export const selectBlpData = createSelector(
  selectElectricalData,
  (electrical) => electrical?.blp || {}
);

export const selectAtsData = createSelector(
  selectElectricalData,
  (electrical) => electrical?.ats || {}
);

export const selectProductionMeterData = createSelector(
  selectElectricalData,
  (electrical) => electrical?.production_meter || {}
);

export const selectUtilityData = createSelector(
  selectElectricalData,
  (electrical) => electrical?.utility || {}
);

export const selectSubpanelData = createSelector(
  selectElectricalData,
  (electrical) => electrical?.subpanel || {}
);

// POI selectors
export const selectPoiData = createSelector(
  selectElectricalData,
  (electrical) => electrical?.poi || {}
);

// Legacy DISCO selectors (deprecated - use multi-disco selectors instead)
export const selectDiscoData = createSelector(
  selectElectricalData,
  (electrical) => ({
    selected_disco: '',
    selected_fuse_rating: ''
  })
);

// Multi-disco selectors
export const selectDisco1Data = createSelector(
  selectElectricalData,
  (electrical) => electrical?.disco_1 || {}
);

export const selectDisco2Data = createSelector(
  selectElectricalData,
  (electrical) => electrical?.disco_2 || {}
);

export const selectDisco3Data = createSelector(
  selectElectricalData,
  (electrical) => electrical?.disco_3 || {}
);

export const selectDisco4Data = createSelector(
  selectElectricalData,
  (electrical) => electrical?.disco_4 || {}
);

export const selectDisco5Data = createSelector(
  selectElectricalData,
  (electrical) => electrical?.disco_5 || {}
);

// Additional Electric Configuration selectors
export const selectAdditionalElectricConfigData = createSelector(
  selectElectricalData,
  (electrical) => ({
    aec_type: electrical.aec_type,
    new_main_breaker_size: electrical.new_main_breaker_size,
    subpanel_size: electrical.subpanel_size,
    feeding_method: electrical.feeding_method,
    ocpd_size: electrical.ocpd_size,
    pv_breaker_size: electrical.pv_breaker_size,
    pv_breaker_location: electrical.pv_breaker_location
  })
);

// Structural selectors
export const selectStructuralData = createSelector(
  selectFormData,
  (formData) => formData?.structural || {}
);

export const selectGeneralConfigurationData = createSelector(
  selectStructuralData,
  (structural) => structural?.general_configuration || {}
);

export const selectBillOfMaterialData = createSelector(
  selectFormData,
  (formData) => {
    const bom = formData?.bom;
    if (bom?.standard) {
      return {
        total_rails: bom.standard.total_rails ?? 0,
        total_splice_kits: bom.standard.total_splice_kits ?? 0,
        total_mid_clamps: bom.standard.total_mid_clamps ?? 0,
        total_end_clamps: bom.standard.total_end_clamps ?? 0,
        total_grounding_lugs: bom.standard.total_grounding_lugs ?? 0,
        is_manual_override: bom.standard.is_manual_override ?? false,
      };
    }
    return DEFAULT_BILL_OF_MATERIAL;
  }
);

export const selectStructuresData = createSelector(
  selectStructuralData,
  (structural) => structural?.structures || {}
);

export const selectStructure1Data = createSelector(
  selectStructuresData,
  (structures) => structures?.structure_1 || {}
);

export const selectStructure2Data = createSelector(
  selectStructuresData,
  (structures) => structures?.structure_2 || {}
);

export const selectStructure3Data = createSelector(
  selectStructuresData,
  (structures) => structures?.structure_3 || {}
);

// Get roof configuration for current structure (structure_1 by default)
export const selectRoofConfigurationData = createSelector(
  selectStructure1Data,
  (structure1) => structure1?.roof_configuration || {}
);

export const selectStructuralInstallationData = createSelector(
  selectStructuralData,
  (structural) => ({})
);

export const selectConduitData = createSelector(
  selectStructuralData,
  (structural) => ({})
);

export const selectAttachmentsRailsData = createSelector(
  selectStructuralData,
  (structural) => ({})
);


// BOM selectors (replaces lender packages)
export const selectBomData = createSelector(
  selectFormData,
  (formData) => formData?.bom || DEFAULT_BOM_DATA
);


export const selectLgcyBomData = createSelector(
  selectBomData,
  (bom) => (bom?.lgcy || DEFAULT_LGCY_BOM) as typeof DEFAULT_LGCY_BOM
);

export const selectLenderPackageItems = createSelector(
  selectLgcyBomData,
  (lgcy) => lgcy?.lender_package_items || []
);

export const selectClientNetsuiteItems = createSelector(
  selectLgcyBomData,
  (lgcy) => lgcy?.client_netsuite_items || []
);

/** Calculated Enphase BOM results from the LGCY BOM (null when the project is not Enphase) */
export const selectLgcyEnphaseBomResults = createSelector(
  selectLgcyBomData,
  (lgcy): LgcyEnphaseBomResults | null => lgcy?.bom_results?.enphase ?? null
);

/** Auto-added Enphase accessory items from the client database selection */
export const selectAutoAddedNetsuiteItems = createSelector(
  selectClientNetsuiteItems,
  (items): NetsuiteItemState[] => items.filter((item: NetsuiteItemState) => item.auto_added)
);

/** Resolved client identity from the selected task */
export const selectTaskClient = createSelector(
  selectSelectedTask,
  (selectedTask) => ({
    client: selectedTask?.client || null,
    client_id: selectedTask?.client_id || null,
    client_name: selectedTask?.client_name || null,
  })
);

/** Whether the current project is in LGCY BOM mode */
export const selectIsLgcyClient = createSelector(
  selectBomData,
  (bom): boolean => bom?.bom_type === 'lgcy'
);

// BOM derived input selectors

/** Total rows of panels across all structures (both panel_rows_1 and panel_rows_2) */
export const selectBomTotalRowsOfPanels = createSelector(
  [selectStructuresData],
  (structures): number => {
    let total = 0;
    const structureKeys = ['structure_1', 'structure_2', 'structure_3'] as const;
    structureKeys.forEach(key => {
      const structure = structures?.[key];
      if (!structure?.attachments_rails) return;
      const rows1 = structure.attachments_rails.panel_rows_1?.rows || [];
      const rows2 = structure.attachments_rails.panel_rows_2?.rows || [];
      total += [...rows1, ...rows2].filter(r => r.panel_count > 0).length;
    });
    return total;
  }
);

/** Total number of defined strings across all systems (non-empty string values) */
export const selectBomStringsCircuits = createSelector(
  selectEquipmentData,
  (equipment): number => {
    let total = 0;
    SYSTEM_KEYS.forEach(key => {
      const strings = equipment?.[key]?.strings;
      if (strings) {
        total += Object.values(strings).filter(v => typeof v === 'string' && v.trim()).length;
      }
    });
    return total;
  }
);

/** MCI-S: RSD quantity for Tesla inverters, 0 otherwise */
export const selectBomMciS = createSelector(
  selectEquipmentData,
  (equipment): number => {
    let total = 0;
    SYSTEM_KEYS.forEach(key => {
      const inverters = equipment?.[key]?.inverters;
      if (inverters?.selected_brand?.name?.toUpperCase() === INVERTER_BRANDS.TESLA && inverters?.rsd?.quantity) {
        total += inverters.rsd.quantity;
      }
    });
    return total;
  }
);

/** Total modules (panels) across all systems */
export const selectBomTotalModules = createSelector(
  selectEquipmentData,
  (equipment): number => {
    let total = 0;
    SYSTEM_KEYS.forEach(key => {
      const qty = equipment?.[key]?.module?.quantity;
      if (qty && qty > 0) total += qty;
    });
    return total;
  }
);

/** Whether any system has Enphase inverters */
export const selectBomIsEnphase = createSelector(
  selectEquipmentData,
  (equipment): boolean => {
    return SYSTEM_KEYS.some(key => {
      const brand = equipment?.[key]?.inverters?.selected_brand?.name;
      return brand?.toUpperCase() === INVERTER_BRANDS.ENPHASE;
    });
  }
);

/** Total inverter quantity across all systems */
export const selectTotalInverters = createSelector(
  selectEquipmentData,
  (equipment): number => {
    let total = 0;
    SYSTEM_KEYS.forEach(key => {
      const inverters = equipment?.[key]?.inverters?.selected_inverters;
      if (inverters) {
        total += inverters.reduce((sum: number, inv: { quantity: number }) => sum + (inv.quantity || 0), 0);
      }
    });
    return total;
  }
);

/** Total microinverter quantity across all systems (excludes regular inverters) */
export const selectTotalMicroinverters = createSelector(
  selectEquipmentData,
  (equipment): number => {
    let total = 0;
    SYSTEM_KEYS.forEach(key => {
      const inv = equipment?.[key]?.inverters;
      if (inv?.selected_type === InverterType.MICROINVERTER && inv?.selected_inverters) {
        total += inv.selected_inverters.reduce((sum: number, i: { quantity: number }) => sum + (i.quantity || 0), 0);
      }
    });
    return total;
  }
);

/** Total optimizer/RSD quantity across all systems (SolarEdge optimizers + Tesla RSDs) */
export const selectTotalOptimizersRsd = createSelector(
  selectEquipmentData,
  (equipment): number => {
    let total = 0;
    SYSTEM_KEYS.forEach(key => {
      const inv = equipment?.[key]?.inverters;
      if (inv?.optimizer_quantity) total += inv.optimizer_quantity;
      if (inv?.rsd?.quantity) total += inv.rsd.quantity;
    });
    return total;
  }
);

/** Total battery quantity across all systems */
export const selectTotalBatteries = createSelector(
  selectEquipmentData,
  (equipment): number => {
    let total = 0;
    SYSTEM_KEYS.forEach(key => {
      const qty = equipment?.[key]?.batteries?.quantity;
      if (qty && qty > 0) total += qty;
    });
    return total;
  }
);

/** Estimated attachments: grounding lugs from base BOM (rows_with_panels * 2) — approximation */
export const selectBomEstimatedAttachments = createSelector(
  [selectStructuresData],
  (structures): number => {
    let rowsWithPanels = 0;
    const structureKeys = ['structure_1', 'structure_2', 'structure_3'] as const;
    structureKeys.forEach(key => {
      const structure = structures?.[key];
      if (!structure?.attachments_rails) return;
      const rows1 = structure.attachments_rails.panel_rows_1?.rows || [];
      const rows2 = structure.attachments_rails.panel_rows_2?.rows || [];
      rowsWithPanels += [...rows1, ...rows2].filter(r => r.panel_count > 0).length;
    });
    // Estimated attachments = rows * 2 (same as grounding lugs formula)
    return rowsWithPanels * 2;
  }
);

// Documentation selectors
export const selectDocumentationData = createSelector(
  selectFormData,
  (formData) => formData?.documentation || {}
);

// CR Output selectors
export const selectCROutput = createSelector(
  selectCreateCR,
  (createCR) => createCR?.cr_output || {}
);

export const selectGeneratedCR = createSelector(
  selectCROutput,
  (crOutput) => crOutput?.generated_cr || {}
);

export const selectUpdatedCR = createSelector(
  selectCROutput,
  (crOutput) => crOutput?.updated_cr || {}
);

export const selectCRChanges = createSelector(
  selectCROutput,
  (crOutput) => crOutput?.changes || {}
);

export const selectActiveTab = createSelector(
  selectCROutput,
  (crOutput) => crOutput?.active_tab
);

// Validation selectors
export const selectIsTaskValid = createSelector(
  [selectSelectedTask],
  (selectedTask) => !!selectedTask
);

// Check if there are unsaved changes
export const selectHasUnsavedChanges = createSelector(
  [selectFormData, selectCurrentStep, selectGeneratedCR, selectUpdatedCR],
  (formData, currentStep, generatedCR, updatedCR) => {
    // If we haven't started or only on task step, no unsaved changes
    if (currentStep === WorkflowStep.TASK) return false;

    // If we have a selected task, there are changes
    if (formData?.task?.selected_task) return true;

    // Check if any system has equipment changes
    const equipment = formData?.equipment;
    const hasEquipmentChanges = SYSTEM_KEYS.some(key => {
      const sys = equipment?.[key];
      return !!(
        sys?.module?.selected_brand ||
        sys?.module?.selected_module ||
        sys?.inverters?.selected_brand ||
        (sys?.inverters?.selected_inverters?.length ?? 0) > 0 ||
        sys?.batteries?.selected_brand ||
        sys?.batteries?.selected_battery
      );
    });

    // Check if any electrical or structural data has been filled
    const hasElectricalChanges = !!(
      formData?.electrical?.msp?.buss_rating ||
      formData?.electrical?.msp?.main_breaker_rating ||
      formData?.electrical?.poi?.selected_poi ||
      formData?.electrical?.disco_1?.model
    );

    const hasStructuralChanges = !!(
      formData?.structural?.general_configuration?.mount_system ||
      formData?.structural?.structures?.structure_1?.name ||
      formData?.structural?.structures?.structure_1?.framing?.size ||
      formData?.structural?.structures?.structure_1?.framing?.type ||
      formData?.structural?.structures?.structure_1?.framing?.rafter_spacing ||
      formData?.structural?.structures?.structure_1?.attachments_rails?.attachment_1 ||
      formData?.structural?.structures?.structure_2?.name ||
      formData?.structural?.structures?.structure_3?.name
    );

    const hasInstallationChanges = hasElectricalChanges || hasStructuralChanges;

    // Check if documentation has been filled
    const hasDocumentationChanges = !!(
      formData?.documentation?.bos_location_value ||
      formData?.documentation?.ahj_value
    );

    // Check if CR has been generated or modified
    const hasCRChanges = !!(generatedCR && Object.keys(generatedCR).length > 0) ||
                        !!(updatedCR && Object.keys(updatedCR).length > 0);

    return hasEquipmentChanges || hasInstallationChanges || hasDocumentationChanges || hasCRChanges;
  }
);

export const selectIsModuleValid = createSelector(
  selectModuleData,
  (moduleData) => !!(moduleData?.selected_brand && moduleData?.selected_module && moduleData?.quantity > 0)
);

// Per-system validation for tab indicators
export const selectSystemsValidation = createSelector(
  selectEquipmentData,
  (equipment): Record<SystemKey, { isModuleValid: boolean; isInverterValid: boolean }> => {
    const result = {} as Record<SystemKey, { isModuleValid: boolean; isInverterValid: boolean }>;
    SYSTEM_KEYS.forEach(key => {
      const sys = equipment?.[key];
      const mod = sys?.module;
      const inv = sys?.inverters;
      result[key] = {
        isModuleValid: !!(mod?.selected_brand && mod?.selected_module && mod?.quantity > 0),
        isInverterValid: !!(inv?.selected_brand && inv?.selected_inverters?.length > 0),
      };
    });
    return result;
  }
);

export const selectIsInverterValid = createSelector(
  selectInverterData,
  (inverterData) => !!(inverterData?.selected_brand && inverterData?.selected_inverters?.length > 0)
);

export const selectIsOptimizerValid = createSelector(
  selectInverterData,
  (inverterData) => {
    // Only check optimizer validity for regular inverters (not microinverters)
    if (inverterData?.selected_type === InverterType.INVERTER) {
      // If SolarEdge is selected, optimizer is required
      if (inverterData?.selected_brand?.name?.toUpperCase() === INVERTER_BRANDS.SOLAREDGE) {
        return !!(inverterData?.selected_optimizer && inverterData?.optimizer_quantity > 0);
      }
      // For Tesla, check RSD fields
      if (inverterData?.selected_brand?.name?.toUpperCase() === INVERTER_BRANDS.TESLA) {
        return !!(inverterData?.rsd?.type?.trim() && inverterData?.rsd?.quantity > 0);
      }
    }
    // For microinverters, check combiner field
    if (inverterData?.selected_type === InverterType.MICROINVERTER) {
      return !!(inverterData?.combiner?.trim());
    }
    // For other cases, return true
    return true;
  }
);

// Complete form data selector for OutputPreview component
export const selectCompleteFormData = createSelector(
  [
    selectTaskData,
    selectEquipmentData,
    selectElectricalData,
    selectStructuralData,
    selectBomData,
    selectDocumentationData
  ],
  (taskData, equipmentData, electricalData, structuralData, bomData, documentationData) => ({
    task: taskData,
    equipment: equipmentData,
    electrical: electricalData,
    structural: structuralData,
    bom: bomData,
    documentation: documentationData
  })
);
